/**
 * Global Footer to use for all HipHerd properties
 *
 * Copyright (c) 2020 Propellant Software Corp
 * Author: Kelvin Nguyen
 */
import React, { useMemo } from 'react'
import PropTypes from 'prop-types'
import cx from 'classnames'
import { Container } from 'reactstrap'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import Link from '../Link'
import Copyright from '../Copyright'
import styles from './Footer.module.scss'

const footerItems = [{
  title: 'Product',
  items: [{
    url: 'https://chrome.google.com/webstore/detail/hipherd-add-to-board/aekgmgaoilbogdmohehiopkekpcfmpfd',
    label: 'Chrome Extension',
    track: 'chromeExtension'
  }, {
    url: '/sitemap/locations',
    label: 'Sitemap',
    track: 'sitemap'
  }]
}, {
  title: 'Company',
  items: [{
    url: 'https://blog.hipherd.com',
    label: 'Blog',
    track: 'blog'
  }, {
    url: 'https://hipherd.kampsite.co',
    label: 'Give Feedback',
    track: 'feedback'
  }, {
    url: 'mailto:support@hipherd.com',
    label: 'Contact',
    track: 'email'
  }]
}, {
  title: 'Legal',
  items: [{
    url: '/s/legal/privacy-policy',
    label: 'Privacy Policy',
    track: 'privacy'
  }, {
    url: '/s/legal/terms',
    label: 'Terms of Use',
    track: 'terms'
  }]
}]

const Footer = ({ container }) => {
  const FooterMenuItems = useMemo(() => (
    footerItems.map(({ title, items }) => (
      <div key={`footerList-${title}`} className={cx(styles['footer-column'])}>
        <h5 className={cx(styles['footer-title'])}>{title}</h5>
        <ul className={cx(styles['footer-list'])}>
          {
            items.map(({ url, label, track }) => (
              <li key={`footerItem-${track}`} className='mb-3'>
                <Link trackProps={{ 'Label': track }} to={url} anchor>
                  <h6 className='font-small darker-gray'>{label}</h6>
                </Link>
              </li>
            ))
          }
        </ul>
      </div>
    ))
  ), [])

  const FooterContent = useMemo(() => (
    <div className={cx(styles['footer-section'])}>
      <div className={cx(styles['flex-brand-container'])}>
        <img src='/static/assets/img/logos/brand/all-black.svg' alt='hipherd-logo' className={cx(styles[`footer-brand-logo`])} />
        <Copyright className='dark-gray' />
        <ul className={cx(styles['social-links'])}>
          <li>
            <Link trackProps={{ 'Label': 'instagram' }} to='https://www.instagram.com/_hipherd' anchor>
              <FontAwesomeIcon icon={['fab', 'instagram']} />
            </Link>
          </li>
          <li>
            <Link trackProps={{ 'Label': 'twitter' }} to='https://twitter.com/hipherd' anchor>
              <FontAwesomeIcon icon={['fab', 'twitter']} />
            </Link>
          </li>
        </ul>
      </div>
      <div className={styles['flex-links-container']}>
        {FooterMenuItems}
      </div>
    </div>
  ), [FooterMenuItems])

  return (
    <section className='bg-barley-white'>
      {container ? (
        <Container className={cx(styles['footer-container'])}>
          {FooterContent}
        </Container>
      ) : FooterContent}
    </section>
  )
}

Footer.propTypes = {
  container: PropTypes.bool
}

export default Footer
